/**
 * Volleyball Position Recommendation Rules
 * 
 * Defines position recommendations for Volleyball players based on their attributes
 */

import type { PositionRule } from '../positionRecommendations';

/**
 * Volleyball Position Rules
 */
export const VOLLEYBALL_POSITION_RULES: PositionRule[] = [
  // PREMIUM POSITIONS - Require elevated thresholds
  {
    position: 'S',
    tier: 'premium',
    description: 'Setter - Requires elite setting, awareness, and court vision',
    requirements: [
      { attribute: 'setting', minValue: 7, required: true },
      { attribute: 'awareness', minValue: 7, required: true },
      { attribute: 'sport_iq', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const setting = attrs.get('setting') || 0;
      const awareness = attrs.get('awareness') || 0;
      const sport_iq = attrs.get('sport_iq') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      return setting * 2.0 + awareness * 1.8 + sport_iq * 1.5 + agility * 1.0 + speed * 0.9;
    },
  },
  {
    position: 'L',
    tier: 'premium',
    description: 'Libero - Requires elite digging, passing, and defensive positioning',
    requirements: [
      { attribute: 'digging', minValue: 7, required: true },
      { attribute: 'passing', minValue: 7, required: true },
      { attribute: 'agility', minValue: 6, required: false },
    ],
    scoreFormula: (attrs) => {
      const digging = attrs.get('digging') || 0;
      const passing = attrs.get('passing') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      const awareness = attrs.get('awareness') || 0;
      return digging * 2.0 + passing * 1.8 + agility * 1.4 + speed * 1.2 + awareness * 1.2;
    },
  },

  // STANDARD POSITIONS - Moderate thresholds
  {
    position: 'OH',
    tier: 'standard',
    description: 'Outside Hitter - Requires spiking, serving, and all-around skills',
    requirements: [
      { attribute: 'spiking', minValue: 6, required: true },
      { attribute: 'serving', minValue: 6, required: true },
      { attribute: 'passing', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const spiking = attrs.get('spiking') || 0;
      const serving = attrs.get('serving') || 0;
      const blocking = attrs.get('blocking') || 0;
      const digging = attrs.get('digging') || 0;
      const agility = attrs.get('agility') || 0;
      return spiking * 1.8 + serving * 1.5 + blocking * 1.2 + digging * 1.1 + agility * 1.0;
    },
  },
  {
    position: 'MB',
    tier: 'standard',
    description: 'Middle Blocker - Requires blocking, quick attacks, and height',
    requirements: [
      { attribute: 'blocking', minValue: 6, required: true },
      { attribute: 'spiking', minValue: 5, required: true },
      { attribute: 'jumping', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const blocking = attrs.get('blocking') || 0;
      const spiking = attrs.get('spiking') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      const strength = attrs.get('strength') || 0;
      return blocking * 2.0 + spiking * 1.4 + agility * 1.3 + speed * 1.0 + strength * 0.9;
    },
  },
  {
    position: 'OP',
    tier: 'standard',
    description: 'Opposite Hitter - Requires spiking, blocking, and serving',
    requirements: [
      { attribute: 'spiking', minValue: 6, required: true },
      { attribute: 'blocking', minValue: 6, required: true },
      { attribute: 'serving', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const spiking = attrs.get('spiking') || 0;
      const blocking = attrs.get('blocking') || 0;
      const serving = attrs.get('serving') || 0;
      const agility = attrs.get('agility') || 0;
      const strength = attrs.get('strength') || 0;
      return spiking * 1.8 + blocking * 1.6 + serving * 1.3 + agility * 1.2 + strength * 0.9;
    },
  },

  // EASIER POSITIONS - Lower thresholds (accessible for less developed players)
  {
    position: 'DS',
    tier: 'easier',
    description: 'Defensive Specialist - Requires digging and passing skills',
    requirements: [
      { attribute: 'digging', minValue: 5, required: true },
      { attribute: 'passing', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const digging = attrs.get('digging') || 0;
      const agility = attrs.get('agility') || 0;
      const speed = attrs.get('speed') || 0;
      const awareness = attrs.get('awareness') || 0;
      const setting = attrs.get('setting') || 0;
      return digging * 1.8 + agility * 1.6 + speed * 1.2 + awareness * 1.1 + setting * 1.0;
    },
  },
  {
    position: 'SS',
    tier: 'easier',
    description: 'Serving Specialist - Requires strong serving ability',
    requirements: [
      { attribute: 'serving', minValue: 5, required: true },
    ],
    scoreFormula: (attrs) => {
      const serving = attrs.get('serving') || 0;
      const focus = attrs.get('focus') || 0; // Mental attribute
      const setting = attrs.get('setting') || 0;
      return serving * 2.0 + focus * 1.0 + setting * 0.8;
    },
  },
];

